import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Cube, LockKey, ArrowLeft } from "@phosphor-icons/react";

import { useI18n } from "@/lib/i18n";
export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!token) setErr(t("final.reset_invalid_token"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.next.length < 10 || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(pw.next) || !/\d/.test(pw.next)) return setErr("Şifre en az 10 karakter, bir harf ve bir rakam içermelidir.");
    if (pw.next !== pw.confirm) return setErr(t("final.reset_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw.next });
      setOk(true);
      toast.success(t("final.reset_done"));
      setTimeout(() => nav("/login"), 1800);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-[#2563EB] flex items-center justify-center rounded-sm">
            <Cube weight="bold" size={20} className="text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-4">H3POE</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("reset.title")}</div>
          </div>
        </div>
        <h1 className="font-display font-extrabold text-2xl mb-2">{t("reset.new_password")}</h1>
        <p className="text-sm text-slate-500 mb-6">{t("reset.desc")}</p>

        {!token || err ? (
          <div className="text-sm bg-red-50 border-l-2 border-red-500 text-red-800 p-3 mb-4" data-testid="reset-error">
            {err || t("reset.invalid")}
          </div>
        ) : null}

        {ok ? (
          <div className="text-sm bg-emerald-50 border-l-2 border-emerald-500 text-emerald-800 p-3" data-testid="reset-success">
            {t("reset.success")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("profile.new_password")}</div>
              <input
                data-testid="reset-new-password"
                type="password" required minLength={10}
                value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                className="mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("profile.confirm_password")}</div>
              <input
                data-testid="reset-confirm-password"
                type="password" required minLength={10}
                value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className="mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm font-mono"
              />
            </label>
            <button
              type="submit" disabled={loading || !token}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-40 text-white py-2.5 font-semibold rounded-sm">
              <LockKey size={15} weight="bold" /> {loading ? t("profile.updating") : t("reset.update")}
            </button>
          </form>
        )}
        <button
          onClick={() => nav("/login")}
          className="mt-6 w-full inline-flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-[#2563EB] font-semibold">
          <ArrowLeft size={12} weight="bold" /> {t("reset.back")}
        </button>
      </div>
    </div>
  );
}
